import { Button, Flex, Text, Box } from "@chakra-ui/react";
import {
  SocialMediaIcons,
  SocialMediaLink,
} from "../../components/navigation/components/SocialMediaIcons";
import { Squiggle170x10Icon } from "../../components/icons/Squiggle170x10Icon";
import {
  useIsSmallBreakpoint,
  useMobileBreakpoint,
} from "../../utils/useBreakpoints";
import { HiHiIcon } from "../../components/icons/HiHiIcon";
import { useToggleArtistFollow } from "../../components/cards/hooks/useToggleArtistFollow";
import { Artist } from "../../types";
import { useQuery, skipToken } from "@tanstack/react-query";
import { FollowingIcon } from "../../components/icons/FollowingIcon";
import { FunnelContextArea } from "../../components/funnel/types";

const hihiMediaLinks: SocialMediaLink[] = [
  {
    platform: "X",
    url: "https://x.com/hihistudios",
  },
  {
    platform: "Instagram",
    url: "https://www.instagram.com/hihistudios",
  },
];
export function HiHiStudios({ artistData }: { artistData?: Artist }) {
  const isSmallBreakpoint = useIsSmallBreakpoint();
  const isMobile = useMobileBreakpoint();

  const artistCacheKey = `${artistData?.entity_type}-${artistData?.id}`;
  const { data: cachedArtistData } = useQuery<Artist>({
    enabled: !!artistData,
    queryKey: [artistCacheKey],
    queryFn: skipToken,
    staleTime: Infinity,
  });
  const { handleToggleArtistFollow } = useToggleArtistFollow(
    artistCacheKey,
    FunnelContextArea.valkyrae,
  );
  const isFollowing = cachedArtistData?.user_is_following;

  return (
    <Flex flexDirection="column" width="100%" alignItems="center" gap="24px">
      <Text variant="landingPageSectionTitle" textAlign="center">
        About Hihi Studios
      </Text>
      <Squiggle170x10Icon
        transform="scaleY(-1)"
        width="172px"
        height="12px"
        color="blaze.blaze"
        marginBottom="24px"
      />
      <Flex
        position="relative"
        maxHeight={isSmallBreakpoint ? "none" : undefined}
        width="100%"
        maxWidth="1440px"
        height="fit-content"
        backgroundRepeat="no-repeat"
        backgroundSize="100% 100%"
        margin="0% auto"
        backgroundImage={`${window.gc.global.assets_url}/static/images/web-ui/valkyrae/hihi_mask.svg`}
        filter="drop-shadow(0px 20px 84px rgba(255, 245, 0, 0.10))"
        style={{
          backgroundPositionX: "center",
          WebkitMaskImage: `url(${window.gc.global.assets_url}/static/images/web-ui/valkyrae/hihi_mask.svg)`,
          WebkitMaskSize: "100% 100%",
          WebkitMaskRepeat: "no-repeat",
          maskImage: `url(${window.gc.global.assets_url}/static/images/web-ui/valkyrae/hihi_mask.svg)`,
          maskSize: "100% 100%",
          maskRepeat: "no-repeat",
        }}
      >
        {isSmallBreakpoint ? null : (
          <Box
            position="absolute"
            bg="black"
            height="100%"
            aspectRatio="1/1"
            style={{
              WebkitMaskImage: `url(${window.gc.global.assets_url}/static/images/web-ui/valkyrae/hihiTexture.svg)`,
              maskImage: `url(${window.gc.global.assets_url}/static/images/web-ui/valkyrae/hihiTexture.svg)`,
              WebkitMaskSize: "100% 100%",
              maskSize: "100% 100%",
              WebkitMaskRepeat: "no-repeat",
              maskRepeat: "no-repeat",
            }}
          />
        )}
        <Flex
          margin="auto"
          py="56px"
          px="70px"
          alignItems={isSmallBreakpoint ? "flex-start" : "center"}
          justifyContent={isSmallBreakpoint ? "center" : "flex-end"}
          flexDirection={isSmallBreakpoint ? "column" : "row"}
          gap="40px"
        >
          <HiHiIcon
            width={isMobile ? "100%" : "300px"}
            height="fit-content"
            zIndex={1}
          />
          <Flex flexDirection="column" gap="16px" flexGrow={1}>
            <Text
              fontFamily="Roboto"
              fontSize="15px"
              fontWeight="500"
              lineHeight="150%"
              color="neutral.900"
              textAlign={isSmallBreakpoint ? "center" : "left"}
            >
              Hihi Studios was founded to create bold, innovative content that
              speaks directly to a global community first drawn in by Valkyrae,
              and now united by the stories we tell. Our mission is to push the
              boundaries of anime and manga-inspired storytelling—crafting
              emotionally resonant, high-quality media that connects with
              millions. That’s why partnering with GlobalComix is a perfect fit.
              Their platform empowers creators and communities alike, aligning
              seamlessly with our vision to expand storytelling beyond
              traditional formats and reach audiences worldwide in fresh,
              meaningful ways.
            </Text>
            <Flex
              alignItems="center"
              flex="1"
              justifyContent={isSmallBreakpoint ? "flex-start" : "space-evenly"}
              flexDirection={isSmallBreakpoint ? "column" : "row"}
              width={isMobile ? "100%" : "80%"}
              gap={isMobile ? "16px" : "40px"}
            >
              <SocialMediaIcons
                mediaLinks={hihiMediaLinks}
                containerProps={{ spacing: "40px", width: "fit-content" }}
                black
              />
              <Flex flex="1">
                <Button
                  background="tranparent"
                  p="8px 24px"
                  borderRadius="12px"
                  border="1px solid"
                  borderColor="transparent.black.40"
                  boxShadow="0px 8px 12px 0px rgba(0, 0, 0, 0.20)"
                  onClick={handleToggleArtistFollow}
                  color="charcoal.charcoal"
                  fontSize="13px"
                  fontWeight="500"
                  lineHeight="11px"
                  letterSpacing=".75px"
                >
                  {isFollowing ? (
                    <FollowingIcon boxSize="20px" color="inherit" />
                  ) : null}
                  {isFollowing
                    ? "Following Hihi Studios"
                    : "Follow Hihi Studios"}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
